import { HistoryEntry, HistoryContext } from './types';

export interface TimeRange {
  start: Date;
  end: Date;
}

export interface Metrics {
  totalEvents: number;
  eventsByType: Record<string, number>;
  averageResponseTime?: number;
  resolutionRate?: number;
  customMetrics: Record<string, number>;
}

export interface TrendData {
  metric: string;
  dataPoints: TrendDataPoint[];
  trend: 'increasing' | 'decreasing' | 'stable';
  changePercentage: number;
  forecast?: TrendDataPoint[];
}

export interface TrendDataPoint {
  timestamp: Date;
  value: number;
  label?: string;
}

export interface Anomaly {
  id: string;
  timestamp: Date;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  value: number;
  expectedValue: number;
  deviation: number;
  context: HistoryContext;
  relatedEntries: string[];
}

export interface Report {
  id: string;
  title: string;
  generatedAt: Date;
  timeRange: TimeRange;
  context: HistoryContext;
  sections: ReportSection[];
  summary: ReportSummary;
}

export interface ReportSection {
  title: string;
  type: 'metrics' | 'chart' | 'table' | 'text';
  data: any;
  insights: string[];
}

export interface ReportSummary {
  keyMetrics: Record<string, number>;
  trends: string[];
  recommendations: string[];
  alerts: string[];
}

export interface ReportTemplate {
  id: string;
  name: string;
  context: HistoryContext;
  sections: ReportSectionTemplate[];
  schedule?: ReportSchedule;
}

export interface ReportSectionTemplate {
  title: string;
  type: 'metrics' | 'chart' | 'table' | 'text';
  config: any;
}

export interface ReportSchedule {
  frequency: 'daily' | 'weekly' | 'monthly';
  recipients: string[];
  enabled: boolean;
}

export class AnalyticsEngine {
  private static instance: AnalyticsEngine;
  private historyData: Map<string, HistoryEntry> = new Map();
  private metricsCache: Map<string, { data: any; timestamp: Date }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.initializeEngine();
  }

  public static getInstance(): AnalyticsEngine {
    if (!AnalyticsEngine.instance) {
      AnalyticsEngine.instance = new AnalyticsEngine();
    }
    return AnalyticsEngine.instance;
  }

  private initializeEngine(): void {
    console.log('Analytics Engine initialized');
  }

  async getMetrics(context: HistoryContext, timeRange: TimeRange): Promise<Metrics> {
    const cacheKey = `metrics_${context}_${timeRange.start.getTime()}_${timeRange.end.getTime()}`;
    
    // Check cache
    const cached = this.metricsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp.getTime() < this.CACHE_TTL) {
      return cached.data;
    }

    try {
      const entries = this.getEntriesInRange(context, timeRange);
      const metrics = this.calculateMetrics(entries, context);
      
      // Cache the result
      this.metricsCache.set(cacheKey, {
        data: metrics,
        timestamp: new Date()
      });
      
      return metrics;
    } catch (error) {
      console.error('Error calculating metrics:', error);
      throw new Error(`Failed to calculate metrics: ${error.message}`);
    }
  }

  async getTrends(context: HistoryContext, metric: string, timeRange: TimeRange): Promise<TrendData> {
    try {
      const entries = this.getEntriesInRange(context, timeRange);
      const dataPoints = this.calculateTrendDataPoints(entries, metric, timeRange);
      const trend = this.analyzeTrend(dataPoints);
      const changePercentage = this.calculateChangePercentage(dataPoints);
      const forecast = this.generateForecast(dataPoints);

      return {
        metric,
        dataPoints,
        trend,
        changePercentage,
        forecast
      };
    } catch (error) {
      console.error('Error calculating trends:', error);
      throw new Error(`Failed to calculate trends: ${error.message}`);
    }
  }

  async detectAnomalies(context: HistoryContext, threshold: number = 2.0): Promise<Anomaly[]> {
    try {
      const entries = this.getEntriesInRange(context, {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
        end: new Date()
      });

      const anomalies: Anomaly[] = [];

      // Detect volume anomalies
      const volumeAnomalies = this.detectVolumeAnomalies(entries, threshold);
      anomalies.push(...volumeAnomalies);

      // Detect response time anomalies (for ticket context)
      if (context === 'ticket') {
        const responseTimeAnomalies = this.detectResponseTimeAnomalies(entries, threshold);
        anomalies.push(...responseTimeAnomalies);
      }

      // Detect payment failure anomalies (for billing context)
      if (context === 'billing') {
        const paymentAnomalies = this.detectPaymentAnomalies(entries, threshold);
        anomalies.push(...paymentAnomalies);
      }

      // Detect pattern anomalies
      const patternAnomalies = this.detectPatternAnomalies(entries, threshold);
      anomalies.push(...patternAnomalies);

      return anomalies.sort((a, b) => b.severity.localeCompare(a.severity));
    } catch (error) {
      console.error('Error detecting anomalies:', error);
      throw new Error(`Failed to detect anomalies: ${error.message}`);
    }
  }

  async generateReport(template: ReportTemplate): Promise<Report> {
    try {
      const timeRange: TimeRange = {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
        end: new Date()
      };

      const sections: ReportSection[] = [];
      const keyMetrics: Record<string, number> = {};
      const trends: string[] = [];
      const recommendations: string[] = [];
      const alerts: string[] = [];

      for (const sectionTemplate of template.sections) {
        const section = await this.generateReportSection(
          sectionTemplate,
          template.context,
          timeRange
        );
        sections.push(section);

        // Extract key metrics
        if (section.type === 'metrics' && section.data) {
          Object.assign(keyMetrics, section.data);
        }

        // Extract insights
        trends.push(...section.insights.filter(insight => insight.includes('trend')));
        recommendations.push(...section.insights.filter(insight => insight.includes('recommend')));
        alerts.push(...section.insights.filter(insight => insight.includes('alert')));
      }

      return {
        id: `report_${Date.now()}`,
        title: template.name,
        generatedAt: new Date(),
        timeRange,
        context: template.context,
        sections,
        summary: {
          keyMetrics,
          trends,
          recommendations,
          alerts
        }
      };
    } catch (error) {
      console.error('Error generating report:', error);
      throw new Error(`Failed to generate report: ${error.message}`);
    }
  }

  // Method to add history entries for analysis
  addHistoryEntry(entry: HistoryEntry): void {
    this.historyData.set(entry.id, entry);
    
    // Clear relevant caches
    this.clearCacheForContext(entry.context);
  }

  private getEntriesInRange(context: HistoryContext, timeRange: TimeRange): HistoryEntry[] {
    return Array.from(this.historyData.values()).filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entry.context === context &&
             entryDate >= timeRange.start &&
             entryDate <= timeRange.end;
    });
  }

  private calculateMetrics(entries: HistoryEntry[], context: HistoryContext): Metrics {
    const metrics: Metrics = {
      totalEvents: entries.length,
      eventsByType: {},
      customMetrics: {}
    };

    // Count events by type
    entries.forEach(entry => {
      metrics.eventsByType[entry.type] = (metrics.eventsByType[entry.type] || 0) + 1;
    });

    // Context-specific metrics
    switch (context) {
      case 'ticket':
        metrics.averageResponseTime = this.calculateAverageResponseTime(entries);
        metrics.resolutionRate = this.calculateResolutionRate(entries);
        metrics.customMetrics.escalationRate = this.calculateEscalationRate(entries);
        break;
      case 'billing':
        metrics.customMetrics.successRate = this.calculatePaymentSuccessRate(entries);
        metrics.customMetrics.averageAmount = this.calculateAveragePaymentAmount(entries);
        metrics.customMetrics.failureRate = this.calculatePaymentFailureRate(entries);
        break;
      case 'template':
        metrics.customMetrics.collaborationIndex = this.calculateCollaborationIndex(entries);
        metrics.customMetrics.changeFrequency = this.calculateChangeFrequency(entries);
        break;
    }

    return metrics;
  }

  private calculateTrendDataPoints(entries: HistoryEntry[], metric: string, timeRange: TimeRange): TrendDataPoint[] {
    const dataPoints: TrendDataPoint[] = [];
    const dayMs = 24 * 60 * 60 * 1000;
    
    // Group entries by day
    const entriesByDay = new Map<string, HistoryEntry[]>();
    
    for (let date = new Date(timeRange.start); date <= timeRange.end; date.setTime(date.getTime() + dayMs)) {
      const dayKey = date.toISOString().split('T')[0];
      entriesByDay.set(dayKey, []);
    }
    
    entries.forEach(entry => {
      const dayKey = new Date(entry.timestamp).toISOString().split('T')[0];
      const dayEntries = entriesByDay.get(dayKey);
      if (dayEntries) {
        dayEntries.push(entry);
      }
    });

    // Calculate metric value for each day
    entriesByDay.forEach((dayEntries, dayKey) => {
      const value = this.calculateMetricValue(dayEntries, metric);
      dataPoints.push({
        timestamp: new Date(dayKey),
        value,
        label: dayKey
      });
    });

    return dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }

  private calculateMetricValue(entries: HistoryEntry[], metric: string): number {
    switch (metric) {
      case 'volume':
        return entries.length;
      case 'responseTime':
        return this.calculateAverageResponseTime(entries) || 0;
      case 'resolutionRate':
        return this.calculateResolutionRate(entries) || 0;
      case 'paymentSuccessRate':
        return this.calculatePaymentSuccessRate(entries) || 0;
      default:
        return entries.length;
    }
  }

  private analyzeTrend(dataPoints: TrendDataPoint[]): 'increasing' | 'decreasing' | 'stable' {
    if (dataPoints.length < 2) return 'stable';
    
    const firstHalf = dataPoints.slice(0, Math.floor(dataPoints.length / 2));
    const secondHalf = dataPoints.slice(Math.floor(dataPoints.length / 2));
    
    const firstAvg = firstHalf.reduce((sum, point) => sum + point.value, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, point) => sum + point.value, 0) / secondHalf.length;
    
    const changeThreshold = 0.05; // 5% change threshold
    const changeRatio = Math.abs(secondAvg - firstAvg) / firstAvg;
    
    if (changeRatio < changeThreshold) return 'stable';
    return secondAvg > firstAvg ? 'increasing' : 'decreasing';
  }

  private calculateChangePercentage(dataPoints: TrendDataPoint[]): number {
    if (dataPoints.length < 2) return 0;
    
    const firstValue = dataPoints[0].value;
    const lastValue = dataPoints[dataPoints.length - 1].value;
    
    if (firstValue === 0) return lastValue > 0 ? 100 : 0;
    
    return ((lastValue - firstValue) / firstValue) * 100;
  }

  private generateForecast(dataPoints: TrendDataPoint[]): TrendDataPoint[] {
    if (dataPoints.length < 3) return [];
    
    // Simple linear regression for forecasting
    const n = dataPoints.length;
    const sumX = dataPoints.reduce((sum, point, index) => sum + index, 0);
    const sumY = dataPoints.reduce((sum, point) => sum + point.value, 0);
    const sumXY = dataPoints.reduce((sum, point, index) => sum + index * point.value, 0);
    const sumXX = dataPoints.reduce((sum, point, index) => sum + index * index, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Generate forecast for next 7 days
    const forecast: TrendDataPoint[] = [];
    const lastDate = dataPoints[dataPoints.length - 1].timestamp;
    
    for (let i = 1; i <= 7; i++) {
      const forecastDate = new Date(lastDate.getTime() + i * 24 * 60 * 60 * 1000);
      const forecastValue = Math.max(0, slope * (n + i - 1) + intercept);
      
      forecast.push({
        timestamp: forecastDate,
        value: forecastValue,
        label: `Forecast ${i}`
      });
    }
    
    return forecast;
  }

  private detectVolumeAnomalies(entries: HistoryEntry[], threshold: number): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const dailyVolumes = this.groupEntriesByDay(entries);
    
    const volumes = Array.from(dailyVolumes.values()).map(dayEntries => dayEntries.length);
    const mean = volumes.reduce((sum, vol) => sum + vol, 0) / volumes.length;
    const stdDev = Math.sqrt(volumes.reduce((sum, vol) => sum + Math.pow(vol - mean, 2), 0) / volumes.length);
    
    dailyVolumes.forEach((dayEntries, date) => {
      const volume = dayEntries.length;
      const zScore = Math.abs((volume - mean) / stdDev);
      
      if (zScore > threshold) {
        anomalies.push({
          id: `volume_anomaly_${date}`,
          timestamp: new Date(date),
          type: 'volume_anomaly',
          severity: this.getSeverityFromZScore(zScore),
          description: `Unusual activity volume: ${volume} events (expected ~${Math.round(mean)})`,
          value: volume,
          expectedValue: mean,
          deviation: zScore,
          context: entries[0]?.context || 'system',
          relatedEntries: dayEntries.map(entry => entry.id)
        });
      }
    });
    
    return anomalies;
  }

  private detectResponseTimeAnomalies(entries: HistoryEntry[], threshold: number): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const responseTimes: number[] = [];
    
    entries.forEach(entry => {
      if (entry.ticketData?.responseTime) {
        responseTimes.push(entry.ticketData.responseTime);
      }
    });
    
    if (responseTimes.length === 0) return anomalies;
    
    const mean = responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length;
    const stdDev = Math.sqrt(responseTimes.reduce((sum, time) => sum + Math.pow(time - mean, 2), 0) / responseTimes.length);
    
    entries.forEach(entry => {
      if (entry.ticketData?.responseTime) {
        const responseTime = entry.ticketData.responseTime;
        const zScore = Math.abs((responseTime - mean) / stdDev);
        
        if (zScore > threshold) {
          anomalies.push({
            id: `response_time_anomaly_${entry.id}`,
            timestamp: new Date(entry.timestamp),
            type: 'response_time_anomaly',
            severity: this.getSeverityFromZScore(zScore),
            description: `Unusual response time: ${responseTime}ms (expected ~${Math.round(mean)}ms)`,
            value: responseTime,
            expectedValue: mean,
            deviation: zScore,
            context: 'ticket',
            relatedEntries: [entry.id]
          });
        }
      }
    });
    
    return anomalies;
  }

  private detectPaymentAnomalies(entries: HistoryEntry[], threshold: number): Anomaly[] {
    const anomalies: Anomaly[] = [];
    const failureEntries = entries.filter(entry => 
      entry.type === 'payment_failed' && entry.billingData
    );
    
    // Detect unusual failure patterns
    const dailyFailures = this.groupEntriesByDay(failureEntries);
    const failureCounts = Array.from(dailyFailures.values()).map(dayEntries => dayEntries.length);
    
    if (failureCounts.length === 0) return anomalies;
    
    const mean = failureCounts.reduce((sum, count) => sum + count, 0) / failureCounts.length;
    const stdDev = Math.sqrt(failureCounts.reduce((sum, count) => sum + Math.pow(count - mean, 2), 0) / failureCounts.length);
    
    dailyFailures.forEach((dayEntries, date) => {
      const failureCount = dayEntries.length;
      const zScore = Math.abs((failureCount - mean) / stdDev);
      
      if (zScore > threshold) {
        anomalies.push({
          id: `payment_failure_anomaly_${date}`,
          timestamp: new Date(date),
          type: 'payment_failure_anomaly',
          severity: this.getSeverityFromZScore(zScore),
          description: `Unusual payment failure rate: ${failureCount} failures (expected ~${Math.round(mean)})`,
          value: failureCount,
          expectedValue: mean,
          deviation: zScore,
          context: 'billing',
          relatedEntries: dayEntries.map(entry => entry.id)
        });
      }
    });
    
    return anomalies;
  }

  private detectPatternAnomalies(entries: HistoryEntry[], threshold: number): Anomaly[] {
    const anomalies: Anomaly[] = [];
    
    // Detect unusual user activity patterns
    const userActivity = new Map<string, number>();
    entries.forEach(entry => {
      userActivity.set(entry.userId, (userActivity.get(entry.userId) || 0) + 1);
    });
    
    const activityCounts = Array.from(userActivity.values());
    if (activityCounts.length === 0) return anomalies;
    
    const mean = activityCounts.reduce((sum, count) => sum + count, 0) / activityCounts.length;
    const stdDev = Math.sqrt(activityCounts.reduce((sum, count) => sum + Math.pow(count - mean, 2), 0) / activityCounts.length);
    
    userActivity.forEach((count, userId) => {
      const zScore = Math.abs((count - mean) / stdDev);
      
      if (zScore > threshold) {
        const userEntries = entries.filter(entry => entry.userId === userId);
        anomalies.push({
          id: `user_activity_anomaly_${userId}`,
          timestamp: new Date(),
          type: 'user_activity_anomaly',
          severity: this.getSeverityFromZScore(zScore),
          description: `Unusual user activity: ${count} events (expected ~${Math.round(mean)})`,
          value: count,
          expectedValue: mean,
          deviation: zScore,
          context: entries[0]?.context || 'system',
          relatedEntries: userEntries.map(entry => entry.id)
        });
      }
    });
    
    return anomalies;
  }

  private async generateReportSection(
    template: ReportSectionTemplate,
    context: HistoryContext,
    timeRange: TimeRange
  ): Promise<ReportSection> {
    const insights: string[] = [];
    let data: any;
    
    switch (template.type) {
      case 'metrics':
        data = await this.getMetrics(context, timeRange);
        insights.push(`Total events: ${data.totalEvents}`);
        if (data.averageResponseTime) {
          insights.push(`Average response time: ${Math.round(data.averageResponseTime)}ms`);
        }
        break;
        
      case 'chart':
        const trendData = await this.getTrends(context, template.config.metric || 'volume', timeRange);
        data = trendData;
        insights.push(`Trend: ${trendData.trend} (${trendData.changePercentage.toFixed(1)}% change)`);
        break;
        
      case 'table':
        const entries = this.getEntriesInRange(context, timeRange);
        data = entries.slice(0, 10); // Top 10 entries
        insights.push(`Showing ${Math.min(10, entries.length)} of ${entries.length} entries`);
        break;
        
      default:
        data = {};
    }
    
    return {
      title: template.title,
      type: template.type,
      data,
      insights
    };
  }

  private groupEntriesByDay(entries: HistoryEntry[]): Map<string, HistoryEntry[]> {
    const grouped = new Map<string, HistoryEntry[]>();
    
    entries.forEach(entry => {
      const date = new Date(entry.timestamp).toISOString().split('T')[0];
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(entry);
    });
    
    return grouped;
  }

  private getSeverityFromZScore(zScore: number): 'low' | 'medium' | 'high' | 'critical' {
    if (zScore > 4) return 'critical';
    if (zScore > 3) return 'high';
    if (zScore > 2) return 'medium';
    return 'low';
  }

  private calculateAverageResponseTime(entries: HistoryEntry[]): number | undefined {
    const responseTimes = entries
      .filter(entry => entry.ticketData?.responseTime)
      .map(entry => entry.ticketData!.responseTime!);
    
    if (responseTimes.length === 0) return undefined;
    
    return responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length;
  }

  private calculateResolutionRate(entries: HistoryEntry[]): number | undefined {
    const resolvedTickets = entries.filter(entry => 
      entry.type === 'status_changed' && entry.ticketData?.statusTo === 'resolved'
    ).length;
    
    const totalTickets = entries.filter(entry => entry.type === 'ticket_created').length;
    
    if (totalTickets === 0) return undefined;
    
    return (resolvedTickets / totalTickets) * 100;
  }

  private calculateEscalationRate(entries: HistoryEntry[]): number {
    const escalatedTickets = entries.filter(entry => 
      entry.type === 'escalated' || (entry.ticketData?.escalationLevel && entry.ticketData.escalationLevel > 0)
    ).length;
    
    const totalTickets = entries.filter(entry => entry.type === 'ticket_created').length;
    
    if (totalTickets === 0) return 0;
    
    return (escalatedTickets / totalTickets) * 100;
  }

  private calculatePaymentSuccessRate(entries: HistoryEntry[]): number {
    const successfulPayments = entries.filter(entry => entry.type === 'payment_successful').length;
    const totalPayments = entries.filter(entry => 
      entry.type === 'payment_successful' || entry.type === 'payment_failed'
    ).length;
    
    if (totalPayments === 0) return 0;
    
    return (successfulPayments / totalPayments) * 100;
  }

  private calculatePaymentFailureRate(entries: HistoryEntry[]): number {
    return 100 - this.calculatePaymentSuccessRate(entries);
  }

  private calculateAveragePaymentAmount(entries: HistoryEntry[]): number {
    const paymentAmounts = entries
      .filter(entry => entry.billingData?.amount)
      .map(entry => entry.billingData!.amount!);
    
    if (paymentAmounts.length === 0) return 0;
    
    return paymentAmounts.reduce((sum, amount) => sum + amount, 0) / paymentAmounts.length;
  }

  private calculateCollaborationIndex(entries: HistoryEntry[]): number {
    const collaborators = new Set<string>();
    entries.forEach(entry => {
      if (entry.templateData?.collaborators) {
        entry.templateData.collaborators.forEach(collab => collaborators.add(collab));
      }
    });
    
    return collaborators.size;
  }

  private calculateChangeFrequency(entries: HistoryEntry[]): number {
    const changes = entries.filter(entry => entry.type === 'template_modified').length;
    const days = 30; // Assuming 30-day period
    
    return changes / days;
  }

  private clearCacheForContext(context: HistoryContext): void {
    const keysToDelete: string[] = [];
    
    this.metricsCache.forEach((value, key) => {
      if (key.includes(context)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.metricsCache.delete(key));
  }
}